import { Select, SelectOption, SelectOptionGroup } from "@amzn/meridian";
import React, { useState, useEffect } from "react";
import { INADEQUATE_INFORMATION, NO_OBJECT_INVOLVED, OTHER } from "src/constants";
import { createSearchRegExp } from "src/helpers";
import { Field } from "src/store/modelManagementSlice";
import { useAppDispatch, useAppSelector } from "src/store/store";
import { compareStringsIgnoreCase } from "src/utils/stringUtil";
import Column from "@amzn/meridian/column";
import Toggle from "@amzn/meridian/toggle";
import { AuditField, AuditResult, setFieldConfirmedAnswer } from "src/store/auditSlice";
import { isConfirmedAnswerValid, isFieldMultiSelected } from "src/components/pages/audit/helpers";
import ChildField from "./childField";
import { getInadequateSelection, otherSelection } from "./selectionHelpers";

interface FullOptionsListProps {
  auditField: AuditField;
  fieldIndex: number;
  shouldShowError: boolean;
}
const FullOptionList = ({ auditField, fieldIndex, shouldShowError }: FullOptionsListProps) => {
  const dispatch = useAppDispatch();
  const { selectedModel } = useAppSelector((state) => state.modelSlice);
  const { alias } = useAppSelector((state) => state.user);
  const { selectedAuditData } = useAppSelector((state) => state.auditSlice);

  const [searchQuery, setSearchQuery] = useState("");
  const [hasOverrideOptions, setHasOverrideOptions] = useState(false);
  const [useOverrideOptions, setUseOverrideOptions] = useState(false);
  const [pendingAnswer, setPendingAnswer] = useState<string | string[] | null>(null);
  const searchRegExp = createSearchRegExp(searchQuery);

  const field = Object.values(selectedModel.fields).find(
    (label: Field) => label.name === auditField.fieldName,
  );

  const many = field?.many || false;

  useEffect(() => {
    if (field?.overrideOptions && field.overrideOptions.length > 0 && !hasOverrideOptions) {
      setHasOverrideOptions(true);
      setUseOverrideOptions(true);
    }
  }, [field?.overrideOptions, hasOverrideOptions]);

  const fieldOptions = field?.options || [];
  const fieldOverrideOptions = field?.overrideOptions || [];
  let displayOptions = useOverrideOptions ? fieldOverrideOptions : fieldOptions;

  const hasOtherOption = fieldOptions.some((option) => compareStringsIgnoreCase(option, OTHER));
  const hasNoObjectInvolved = fieldOptions.some((option) =>
    compareStringsIgnoreCase(option, NO_OBJECT_INVOLVED),
  );
  const hasInadequateInfo = fieldOptions.some((option) =>
    compareStringsIgnoreCase(option, INADEQUATE_INFORMATION),
  );

  const isChildField = selectedModel.hierarchy?.some(
    (h) => h.childFieldName === auditField.fieldName && h.parentFieldName === "primary object",
  );

  // Use pendingAnswer if available, otherwise fall back to confirmedAnswer
  let value: string | string[] =
    pendingAnswer !== null ? pendingAnswer : auditField.confirmedAnswer;

  // Handle the case where we're falling back to confirmedAnswer and need to format it
  if (pendingAnswer === null) {
    if (many) {
      if (typeof auditField.confirmedAnswer === "string") {
        value = auditField.confirmedAnswer.includes("|")
          ? auditField.confirmedAnswer.split("|")
          : auditField.confirmedAnswer
            ? [auditField.confirmedAnswer]
            : [];
      } else if (Array.isArray(auditField.confirmedAnswer)) {
        value = auditField.confirmedAnswer;
      } else {
        value = [];
      }
    } else {
      value =
        typeof auditField.confirmedAnswer === "string"
          ? auditField.confirmedAnswer
          : Array.isArray(auditField.confirmedAnswer) && auditField.confirmedAnswer.length > 0
            ? auditField.confirmedAnswer[0]
            : "";
    }
  }

  const clearChildFields = (parentFieldName: string) => {
    if (!selectedModel.hierarchy) return;
    const childFields = selectedModel.hierarchy
      .filter((h) => h.parentFieldName === parentFieldName)
      .map((h) => h.childFieldName);

    childFields.forEach((childFieldName) => {
      const childFieldIndex = selectedAuditData.auditFields.findIndex(
        (field: AuditField) => field.fieldName === childFieldName,
      );

      if (childFieldIndex !== -1) {
        const childField = selectedAuditData.auditFields[childFieldIndex];
        const isMultiSelect = isFieldMultiSelected(childField, selectedModel);

        dispatch(
          setFieldConfirmedAnswer({
            fieldIndex: childFieldIndex,
            confirmedAnswer: isMultiSelect ? [] : "",
            alias: alias,
            auditResult: AuditResult.NEITHER,
          }),
        );
      }
    });
  };

  const onSelectChange = (answer: string | string[]) => {
    setPendingAnswer(answer);
  };

  const onBlur = () => {
    if (pendingAnswer !== null) {
      dispatch(
        setFieldConfirmedAnswer({
          fieldIndex: fieldIndex,
          confirmedAnswer: pendingAnswer,
          alias: alias,
          auditResult: AuditResult.NEITHER,
        }),
      );
      clearChildFields(auditField.fieldName);
      setPendingAnswer(null);
    }
  };

  if (isChildField && selectedModel?.hierarchy) {
    return (
      <ChildField
        auditField={auditField}
        fieldIndex={fieldIndex}
        value={value}
        hasInadequateInfo={hasInadequateInfo}
        shouldShowError={shouldShowError}
        hasOtherOption={hasOtherOption}
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
      />
    );
  }

  return (
    <Column alignmentVertical={"top"}>
      {hasOverrideOptions && (
        <Toggle size="small" checked={useOverrideOptions} onChange={setUseOverrideOptions}>
          {"load site config"}
        </Toggle>
      )}
      <Select
        value={value}
        onChange={onSelectChange}
        onBlur={onBlur}
        placeholder={"Choose"}
        size="medium"
        width={"200px"}
        searchQuery={searchQuery}
        onSearch={setSearchQuery}
        error={shouldShowError && !isConfirmedAnswerValid(auditField)}
        errorMessage={shouldShowError && !auditField.confirmedAnswer && "please select an answer"}
      >
        {getInadequateSelection()}
        <SelectOptionGroup label={"Options"}>
          {displayOptions
            .filter((option) => searchRegExp.test(option))
            .filter((option) => !compareStringsIgnoreCase(option, INADEQUATE_INFORMATION))
            .filter((option) => !compareStringsIgnoreCase(option, OTHER))
            .map((option) => (
              <SelectOption value={option} label={option} />
            ))}
        </SelectOptionGroup>
        {hasOtherOption && otherSelection()}
      </Select>
    </Column>
  );
};

export default FullOptionList;
